// src/encounter/services/unsaved-note.ts
//
// D-73 (design desk, 6 Oct 2026, amended) · frame v2 only: what the in-app leave guard needs to know about the
// note, and the flushes it runs before deciding (encounter-leave-guard.tsx). One small store, because the guard
// sits beside the page (in the encounter route, inside the data router) and the dialog draft sits deep inside it.
//   · the PAGE publishes its note save state (dirty / saving / failed / can save) and registers its autosave
//     flush, only with frame v2 on (`active`);
//   · a DIALOG with a changed, unsaved draft marks it (the "Add Text Notes" dialog);
//   · an INLINE editor that holds typed text for a moment before writing it (a debounced line) registers a flush
//     that commits it now and says whether it had anything to commit — so text typed just before Back is saved,
//     never lost (lane D's inline lines hold up to 500 ms).
import { useCallback, useEffect, useRef } from 'react';
import { create } from 'zustand';

export interface UnsavedNoteState {
  /** The page is mounted with frame v2 on. Off → the guard never blocks. */
  active: boolean;
  /** Answers changed since the last successful save. */
  dirty: boolean;
  /** A note save is in flight. */
  saving: boolean;
  /** The last note save failed. */
  failed: boolean;
  /** The page could send a save now if dirty (no sign, load or refusal latch). */
  canSave: boolean;
  /** Dialogs holding a changed, unsaved draft, by id. */
  drafts: Readonly<Record<string, true>>;
  /** An in-page change held until the clinician answers "Discard this text?" (`confirmNoteViewChange`). */
  pendingViewChange: (() => void) | null;
  publish: (p: Partial<Pick<UnsavedNoteState, 'active' | 'dirty' | 'saving' | 'failed' | 'canSave'>>) => void;
  setDraftChanged: (id: string, changed: boolean) => void;
  reset: () => void;
}

const IDLE = { active: false, dirty: false, saving: false, failed: false, canSave: false, drafts: {}, pendingViewChange: null };

export const useUnsavedNote = create<UnsavedNoteState>((set) => ({
  ...IDLE,
  publish: (p) => set(p),
  setDraftChanged: (id, changed) =>
    set((s) => {
      if (changed === !!s.drafts[id]) return s;
      const drafts = { ...s.drafts };
      if (changed) drafts[id] = true;
      else delete drafts[id];
      return { drafts };
    }),
  reset: () => set(IDLE),
}));

/** Draft ids of an open, unfinished phrase occurrence. A phrase is client-only until Complete; leaving drops it. */
export const PHRASE_DRAFT_PREFIX = 'phrase-draft:';

/** Is one of the drafts an unfinished phrase (the guard then says so)? */
export function hasPhraseDraft(drafts: Readonly<Record<string, true>>): boolean {
  return Object.keys(drafts).some((id) => id.startsWith(PHRASE_DRAFT_PREFIX));
}

// ── In-page changes (#689 follow-up, Andrew 10 Oct) ────────────────────────────────────────────────────────────────
// An open phrase lives only in its line until Complete, so a change on the SAME page that takes the note off screen
// drops it as surely as leaving the visit: Back to the pre-encounter, a centre panel (Generated note, Clinical edits),
// another workspace tab, another section (one section at a time). The router guard never sees these (the path does not
// change), so each of them goes through `confirmNoteViewChange`: with a filled phrase open it asks first, with the
// same "Discard this text?" as a leave; otherwise it goes on at once. A step change in one scroll keeps every section
// on screen, so it never asks.

/** True while a confirmed change runs: a nested change it makes (a section pick that also moves the step) goes on. */
let viewChangeConfirmed = false;

/** Run `go` now, or, while a filled phrase is open (frame v2), only once the clinician picks Discard. */
export function confirmNoteViewChange(go: () => void): void {
  const s = useUnsavedNote.getState();
  if (viewChangeConfirmed || !s.active || !hasPhraseDraft(s.drafts)) {
    go();
    return;
  }
  useUnsavedNote.setState({ pendingViewChange: go });
}

/** The clinician's answer to a held in-page change: Discard runs it (the phrase goes), Keep editing drops it. */
export function settleNoteViewChange(discard: boolean): void {
  const go = useUnsavedNote.getState().pendingViewChange;
  useUnsavedNote.setState({ pendingViewChange: null });
  if (!discard || !go) return;
  viewChangeConfirmed = true;
  try {
    go();
  } finally {
    viewChangeConfirmed = false;
  }
}

/** Anything that leaving now could lose: unsaved answers, a save in flight or failed, or a changed dialog draft. */
export function hasUnsavedNote(s: Pick<UnsavedNoteState, 'dirty' | 'saving' | 'failed' | 'drafts'>): boolean {
  return s.dirty || s.saving || s.failed || Object.keys(s.drafts).length > 0;
}

// ── Flushes ─────────────────────────────────────────────────────────────────────────────────────────────────────

const inlineFlushers = new Map<string, () => boolean | void>();
let noteFlush: (() => void) | null = null;

/** An inline editor's flush: commit any held text now; return true when there was something to commit. */
export function registerInlineDraftFlush(id: string, flush: () => boolean | void): () => void {
  inlineFlushers.set(id, flush);
  return () => {
    if (inlineFlushers.get(id) === flush) inlineFlushers.delete(id);
  };
}

/** The page's note autosave flush (send a dirty note now). */
export function registerNoteFlush(flush: () => void): () => void {
  noteFlush = flush;
  return () => {
    if (noteFlush === flush) noteFlush = null;
  };
}

/** Is a text field of the note focused (a field that commits what was typed when it is left)? */
export function focusedNoteField(): HTMLElement | null {
  const el = typeof document === 'undefined' ? null : (document.activeElement as HTMLElement | null);
  if (!el || !el.closest('[data-structured-note]')) return null;
  const editable = el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable;
  return editable ? el : null;
}

/**
 * Commit every inline draft now: each registered inline flush, then the focused note field is left (a field that
 * commits on leave writes what was typed). Returns true when anything may have been committed.
 */
let lastFlushFailed = false;

/** Did an inline flush throw during the last `flushInlineDrafts`? Its text may not have been committed. */
export function inlineFlushFailed(): boolean {
  return lastFlushFailed;
}

export function flushInlineDrafts(): boolean {
  let any = false;
  lastFlushFailed = false;
  for (const flush of [...inlineFlushers.values()]) {
    try {
      if (flush()) any = true;
    } catch {
      // A failing flush must not stop the others. Its text may still be held, so it counts as unsaved and the guard
      // asks (Codex P2, 6 Oct) — never a silent leave.
      lastFlushFailed = true;
      any = true;
    }
  }
  const field = focusedNoteField();
  if (field) {
    field.blur();
    any = true;
  }
  return any;
}

/** Send the note's pending save now (the page's autosave flush). */
export function flushNote(): void {
  noteFlush?.();
}

/** Hook form of `registerInlineDraftFlush`, for an inline editor: the latest `flush` is always the one called. */
export function useInlineDraftFlush(id: string, flush: () => boolean | void): void {
  const ref = useRef(flush);
  useEffect(() => {
    ref.current = flush;
  });
  useEffect(() => registerInlineDraftFlush(id, () => ref.current()), [id]);
}

/**
 * PROD-2947 (integration, Codex P1) · frame v2: a sign never races a held line. Text typed in an inline line is held up
 * to INLINE_NOTE_SAVE_MS before it is written; a sign pressed inside that window used to freeze the line first, and the
 * line then wrote its draft into the signed note. Call the returned function at the start of the sign: when a held
 * line had something to commit it is committed now, and the sign is HELD (returns true) until the writes it started
 * have settled (`busy` false), then `proceed` runs the sign again. A write that failed stops the sign (`failed`), so
 * the clinician sees the failure first. Nothing held → false, and the sign goes on as before.
 */
/** How long a held sign waits for the writes a flush started to report themselves (the query client announces a
 *  started write on its next tick); with none reported by then, the flush started no write and the sign goes on. */
export const SIGN_HOLD_GRACE_MS = 250;

export function useSignAfterFlush(opts: {
  enabled: boolean;
  busy: boolean;
  failed: boolean;
  proceed: () => void;
  /** A held sign that a failed write stopped: the page says so (smoke test 21 Oct: a stopped sign was silent). */
  onStopped?: () => void;
}): () => boolean {
  // Refs, not state: the hold is decided in the click handler and settled by the writes' own state, never set from an
  // effect.
  const waiting = useRef(false);
  // Only a write that ran while the sign was held can stop it (an older failure is not this sign's business).
  const sawBusy = useRef(false);
  const grace = useRef<ReturnType<typeof setTimeout> | null>(null);
  const proceedRef = useRef(opts.proceed);
  const stoppedRef = useRef(opts.onStopped);
  // The writes' state at the click, not at the last memo: a field left by pressing Sign has just started its write
  // (Codex P1, round 2), so the sign holds for writes ALREADY in flight too.
  const busyRef = useRef(opts.busy);
  useEffect(() => {
    proceedRef.current = opts.proceed;
    stoppedRef.current = opts.onStopped;
    busyRef.current = opts.busy;
  });
  const settle = (stop: boolean) => {
    waiting.current = false;
    sawBusy.current = false;
    if (grace.current) clearTimeout(grace.current);
    grace.current = null;
    if (stop) stoppedRef.current?.();
    else proceedRef.current();
  };
  const settleRef = useRef(settle);
  useEffect(() => {
    settleRef.current = settle;
  });
  useEffect(() => {
    if (!waiting.current) return;
    if (opts.busy) {
      sawBusy.current = true;
      return;
    }
    if (sawBusy.current) settleRef.current(opts.failed);
  }, [opts.busy, opts.failed]);
  useEffect(() => () => {
    if (grace.current) clearTimeout(grace.current);
  }, []);
  const { enabled } = opts;
  return useCallback(() => {
    if (!enabled) return false;
    if (waiting.current) return true;
    const flushed = flushInlineDrafts();
    if (!flushed && !busyRef.current) return false;
    waiting.current = true;
    sawBusy.current = busyRef.current;
    grace.current = setTimeout(() => {
      grace.current = null;
      // No write reported itself: nothing was in flight, so the sign goes on.
      if (waiting.current && !sawBusy.current) settleRef.current(false);
    }, SIGN_HOLD_GRACE_MS);
    return true;
  }, [enabled]);
}
